'use client';

/**
 * The ad slot (ads build plan section 2.3) and its "Why this ad" panel.
 *
 * ONE LINE OF TEXT IN PLATFORM CHROME. The slot is a `min-w-0 flex-1` region
 * that always takes the free middle of its bar, so filling or emptying it
 * moves nothing around it. It draws an `Ad` chip, the advertiser, a middle
 * dot and the headline as ONE link to our own click route, then an (i) button
 * that opens the panel. Below `minWidth` (360 in a title bar, 160 in an app
 * bar) or when not `active` it draws nothing inside. The chip and the
 * advertiser never truncate: the headline truncates first, and if the fixed
 * parts still do not fit the slot stays empty (measured, `fits`).
 *
 * TEXT ONLY, AND NOTHING CROSSES THE BRIDGE. Every string is rendered as React
 * text; there is no `dangerouslySetInnerHTML` here and no script. The app in
 * the frame below cannot see, cover or click the slot, and the slot never
 * talks to the frame (plan section 2.6 rules 1 and 2). The response is
 * checked client-side as well (`readSlotResponse`): anything but `render:
 * 'text'` is dropped, so a future source that answered a frame URL would be
 * refused here as well as on the server.
 *
 * ONE FETCH PER (placement, app) to begin with, through a module-level cache
 * that also deduplicates in-flight requests. 204 and errors are an empty
 * slot, silently.
 *
 * REFRESH (the owner's decision, 2026-09-30; components/ads/ad-refresh.ts,
 * lib/ads/refresh.ts). A drawn ad runs a clock that counts only VIEWABLE
 * time (visible, focused, uncovered, at its minimum width, with a person
 * active) and, at the configured interval the server answered with, asks
 * for its next ad (`?refresh=N&trigger=time&prev=<id>`); a navigation (the
 * slot active again, the tab back in view or focus) asks with
 * `trigger=navigation` once the ad has been in view 45 s. The swap is a
 * 200 ms crossfade in place, instant under reduced motion, never while the
 * pointer is over the ad, focus is inside it or the panel is open; the
 * region's box never changes, so nothing else in the bar moves. A 204 on a
 * refresh empties the slot, which is also how flipping the gate to Nobody
 * now reaches an ad already drawn.
 *
 * THE PICTURE. An ad may carry a small picture (`image.url`, always our own
 * `/api/ads/image/<token>` route, never an advertiser's server), drawn
 * between the Ad label and the text, 24 px tall in the title bar and 28 in
 * the 44 px app bar (`pictureHeight`), its width at most 1.5 times its
 * height with the aspect ratio kept, a small radius and a hairline border so
 * it never reads as an app icon (which is at the far left of a bar, before
 * the name; the picture always follows the Ad label). When room is short the
 * picture goes first, then the headline truncates as before, then the slot
 * empties as before.
 *
 * IMPRESSION AND VIEWABLE (lane E, plan section 5.1) are measured by
 * `useAdTrackers` (components/ads/ad-trackers.ts): an impression beacon once
 * the drawn ad has rendered where a person could see it, and the MRC rule on
 * the ad itself (at least 50% of it intersecting, the document visible, the
 * window focused, for one continuous second, sampled every 100 ms), then ONE
 * `navigator.sendBeacon` per token. Both also need the ad UNCOVERED: hit
 * tests at its centre and two inner points must land inside it (the
 * 2026-09-30 fix round; an ad under the Admin dialog had counted). A hidden
 * window, an unfocused tab, a covered ad or a slot that did not fit never
 * counts. A browser-direct network's pixel fires only from the impression
 * beacon's answer, as a cookieless image.
 *
 * THE LINK carries `rel="sponsored noopener noreferrer"` and opens a new tab.
 * `noreferrer` plus the click route's `Referrer-Policy: no-referrer` is how
 * the advertiser learns nothing about the viewer (rule 9).
 *
 * "WHY THIS AD" carries the full DSA Article 26 set although Article 19 exempts
 * us today (plan decision 5): that it is an ad, the advertiser, who paid, how
 * it was chosen (contextual, from the app's category), that it is not based on
 * personal data, what can be changed (hide, signed in only), and a report
 * link with four reasons. A popover on desktop, a sheet on a phone, Esc
 * closes it and focus returns to the (i) button. No animation, so reduced
 * motion needs no special case; every colour is a theme token.
 *
 * IN THE 44 PX APP BAR (components/workspace/app-bar, lanes B and C) the same
 * component runs with `minWidth` 160, a smaller `clearance`, `align` 'start'
 * and `reserveMinWidth`: the bar's title fades to keep the slot its 160 px
 * (the owner's decision of 2026-09-29), and the reservation is what lets a
 * long app name coexist with an ad at all. Every one of those props has a
 * default that reproduces the title-bar behaviour, so lane A's caller is
 * untouched.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Info, X } from 'lucide-react';

import { useAuth } from '@/components/auth-provider';
import { useT } from '@/components/i18n-provider';
import { BottomSheetShell } from '@/components/ui/bottom-sheet-shell';
import { PopoverPortal, useAnchorRect } from '@/components/ui/popover-portal';
import { useCoarsePointer } from '@/hooks/use-coarse-pointer';
import { useIsomorphicLayoutEffect } from '@/hooks/use-isomorphic-layout-effect';
import {
  AD_REPORT_REASONS,
  AD_SOURCES,
  type AdPlacement,
  type AdRefreshTrigger,
  type AdReportReason,
  type AdSlotRefresh,
  type AdSlotResponse,
  type ServedAd,
  type ServedAdImage,
} from '@/lib/ads/types';
import { cn } from '@/lib/utils';

import { _resetAdRefreshForTests, CROSSFADE_MS, nextFrame, prefersReducedMotion, useAdRefreshClock } from './ad-refresh';
import { _resetAdTrackersForTests, useAdTrackers } from './ad-trackers';

/** The free middle a title bar must offer before an ad is drawn (plan section 2.3). */
export const AD_SLOT_MIN_WIDTH_TITLE_BAR = 360;
/** The same for the 44 px app bar of lanes B and C. */
export const AD_SLOT_MIN_WIDTH_APP_BAR = 160;
/** The small picture's height in the 40 px title bar; the 44 px app bar passes `AD_PICTURE_HEIGHT_APP_BAR`. */
export const AD_PICTURE_HEIGHT_TITLE_BAR = 24;
/** The small picture's height in the 44 px app bar (phone, full screen, the app's own address). */
export const AD_PICTURE_HEIGHT_APP_BAR = 28;
/** The picture's width is at most this many times its height (the aspect ratio is kept inside that box). */
export const AD_PICTURE_MAX_ASPECT = 1.5;
/** Every picture URL the slot will draw starts with this: our own image route, under a signed token. */
export const AD_IMAGE_PATH = '/api/ads/image/';

const PANEL_WIDTH = 320;
const MAX_TEXT = 256;
/** The most refreshes one slot asks for in a page session; past it the ad simply stays (lib/ads/refresh.ts refuses more anyway). */
const MAX_REFRESHES = 10_000;
/**
 * The least width a truncated headline may keep, about six characters at
 * 11 px. Under it the headline is dropped whole rather than shown as a
 * letter and an ellipsis.
 */
const MIN_HEADLINE_PX = 40;

/**
 * True when the drawn row fits its box. Only real content counts: the link
 * must hold its own text, and the row's children must end inside the row.
 * NOT `row.scrollWidth`: the (i)'s touch area is a pseudo-element that
 * reaches into the slot's clearance on purpose, and scrollWidth counts it as
 * overflow, which marked every ad "no fit" and hid it (caught at 375 px on
 * 2026-09-30, the same day the touch areas were added). Layout numbers
 * (`offsetLeft`, `offsetWidth`), so a zoomed canvas reads the same.
 */
function rowFits(row: HTMLElement): boolean {
  const link = row.querySelector('a');
  if (link && link.scrollWidth > link.clientWidth + 1) return false;
  let end = 0;
  for (const child of Array.from(row.children)) {
    const el = child as HTMLElement;
    end = Math.max(end, el.offsetLeft + el.offsetWidth);
  }
  return end <= row.clientWidth + 1;
}

export interface AdSlotProps {
  placement: AdPlacement;
  /** The app shown in this window (`widgetContentId`); null draws nothing. */
  appId: string | null;
  minWidth?: number;
  /** Only the top window on the device is active (`useTopAdWindowId`). */
  active: boolean;
  /** A fixed ad for the admin preview: no fetch, no counting, an inert link. */
  preview?: ServedAd | null;
  className?: string;
  /**
   * The clearance INSIDE the region on each side of a drawn ad, in px. The
   * title bar keeps the default 40 + 40 (plus the bar's 8 px gap: 48 px from
   * the title and from the pills). The 44 px app bar (lanes B and C) has no
   * 48 px to spare on a phone, so it passes a smaller pair. Applied only while
   * an ad is drawn, so an empty slot is still a zero-size spacer.
   */
  clearance?: { start: number; end: number };
  /**
   * Keep the region at least `minWidth` wide while an ad may still come (the
   * fetch is pending, or an ad is drawn), and let it collapse to nothing once
   * the answer is "no ad" or the ad was hidden. The app bar needs this: its
   * title fades to make room for the slot (the owner's title-fade decision,
   * 2026-09-29), and without a reservation a long app name would measure the
   * region below `minWidth` before the fetch, so no ad could ever arrive.
   * With no reservation the region only ever grows into free space, exactly
   * as before.
   */
  reserveMinWidth?: boolean;
  /** Where the ad sits inside a wider region: centred (title bar) or at the start (phone app bar). */
  align?: 'center' | 'start';
  /** The small picture's height in px: 24 in the 40 px title bar (default), 28 in the 44 px app bar. */
  pictureHeight?: number;
}

const DEFAULT_CLEARANCE = { start: 40, end: 40 } as const;

/**
 * The slot region's LAYOUT width: its border box in its own CSS px
 * (`offsetWidth`), whatever transform or zoom lies over it.
 *
 * NOT THE ON-SCREEN BOX (fix round 2, 2026-09-30). The slot measured
 * `getBoundingClientRect().width`, which includes every ancestor transform,
 * and the canvas draws every window inside React Flow's
 * `transform: translate(...) scale(zoom)`. After a visit to Stage (a screen's
 * card clicked, the glide back from the 0.284 overview zoom not yet begun)
 * the title-bar slot became active, stored 351 px for a real 1231 px, stayed
 * under its 360 px minimum and never asked; and a ResizeObserver never fires
 * for a transform-only change, so nothing measured it again: an active, empty
 * slot until the next reload. Measured in Chrome 154 on a 1231 px region under
 * `scale(0.284)`: getBoundingClientRect 349.6, offsetWidth 1231, and the same
 * 1231 under a CSS `zoom` of 0.284. In Chromium 152 the ResizeObserver
 * reported borderBoxSize 1231 and contentBoxSize 1151, and a change of the
 * scale fired no observation. The content box is the wrong box here as well:
 * it leaves out the 2 x 40 px clearance, which exists only while an ad is
 * drawn, so it would shrink the moment an ad appeared.
 */
export function slotLayoutWidth(el: HTMLElement): number {
  return el.offsetWidth;
}

// ---------------------------------------------------------------------------
// Module state: one fetch per (placement, app), one beacon per token, and the
// ads this person hid in this page session (the server keeps the durable list).
// ---------------------------------------------------------------------------

const slotCache = new Map<string, Promise<AdSlotResponse | null>>();
const hiddenAds = new Set<string>();

/** Test hook: forget every cached slot, beacon and hide. */
export function _resetAdSlotStateForTests(): void {
  slotCache.clear();
  hiddenAds.clear();
  _resetAdTrackersForTests();
  _resetAdRefreshForTests();
}

function text(v: unknown, max = MAX_TEXT): string | null {
  return typeof v === 'string' && v.length <= max ? v : null;
}

function positiveInt(v: unknown, max: number): number | undefined {
  return typeof v === 'number' && Number.isInteger(v) && v > 0 && v <= max ? v : undefined;
}

/** The picture, or nothing: only a URL of our own image route is ever drawn. */
function readImage(v: unknown): ServedAdImage | undefined {
  if (typeof v !== 'object' || v === null) return undefined;
  const i = v as Record<string, unknown>;
  const url = text(i.url, 4200);
  if (!url || !url.startsWith(AD_IMAGE_PATH)) return undefined;
  const width = positiveInt(i.width, 8192);
  const height = positiveInt(i.height, 8192);
  return { url, ...(width ? { width } : {}), ...(height ? { height } : {}) };
}

/** The refresh fields; an answer without them (an older pod during a roll) reads as timed refresh off. */
function readRefresh(v: unknown): AdSlotRefresh {
  if (typeof v !== 'object' || v === null) return { intervalSec: 0, ordinal: 0 };
  const r = v as Record<string, unknown>;
  const intervalSec = typeof r.intervalSec === 'number' && Number.isInteger(r.intervalSec) && r.intervalSec >= 0 && r.intervalSec <= 3600 ? r.intervalSec : 0;
  const ordinal = positiveInt(r.ordinal, MAX_REFRESHES) ?? 0;
  // The idle cooldown: 60 to 600 s, else absent and the clock keeps its default.
  const idleSec = typeof r.idleSec === 'number' && Number.isInteger(r.idleSec) && r.idleSec >= 60 && r.idleSec <= 600 ? r.idleSec : undefined;
  return idleSec === undefined ? { intervalSec, ordinal } : { intervalSec, ordinal, idleSec };
}

/** The response, or null for anything that is not a text ad from our own click route. */
export function readSlotResponse(json: unknown): AdSlotResponse | null {
  if (typeof json !== 'object' || json === null) return null;
  const r = json as Record<string, unknown>;
  if (r.render !== 'text') return null;
  const token = text(r.token, 2048);
  const clickUrl = text(r.clickUrl, 2048);
  if (!token || !clickUrl || !clickUrl.startsWith('/api/ads/click/')) return null;
  const a = r.ad;
  if (typeof a !== 'object' || a === null) return null;
  const ad = a as Record<string, unknown>;
  const id = text(ad.id, 64);
  const advertiser = text(ad.advertiser);
  const headline = text(ad.headline);
  const paidBy = text(ad.paidBy);
  if (!id || !advertiser || !headline || !paidBy) return null;
  const source = AD_SOURCES.find((s) => s === ad.source);
  if (!source) return null;
  const signals = Array.isArray(ad.signals)
    ? ad.signals.filter((s): s is 'country' | 'language' => s === 'country' || s === 'language')
    : [];
  const image = readImage(ad.image);
  return {
    render: 'text',
    token,
    clickUrl,
    refresh: readRefresh(r.refresh),
    ad: {
      id,
      source,
      advertiser,
      headline,
      description: text(ad.description) ?? '',
      displayHost: text(ad.displayHost) ?? '',
      paidBy,
      category: text(ad.category, 128),
      ...(signals.length > 0 ? { signals } : {}),
      ...(image ? { image } : {}),
    },
  };
}

function slotUrl(placement: AdPlacement, appId: string): string {
  return `/api/ads/slot?placement=${encodeURIComponent(placement)}&app=${encodeURIComponent(appId)}`;
}

function readSlot(url: string): Promise<AdSlotResponse | null> {
  return fetch(url, { credentials: 'same-origin', cache: 'no-store' })
    .then(async (res) => (res.status === 200 ? readSlotResponse(await res.json()) : null))
    .catch(() => null);
}

function fetchSlot(placement: AdPlacement, appId: string): Promise<AdSlotResponse | null> {
  const key = `${placement}:${appId}`;
  let pending = slotCache.get(key);
  if (!pending) {
    pending = readSlot(slotUrl(placement, appId));
    slotCache.set(key, pending);
  }
  return pending;
}

/**
 * The slot's NEXT ad (the refresh round): never deduplicated, always a new
 * request with the count since page load, what asked, and the ad shown
 * until now. The answer replaces the cached one, so a remount of this
 * (placement, app) draws the latest ad, not the first.
 */
function fetchNextSlot(placement: AdPlacement, appId: string, ordinal: number, trigger: AdRefreshTrigger, prevAdId: string): Promise<AdSlotResponse | null> {
  const key = `${placement}:${appId}`;
  const pending = readSlot(`${slotUrl(placement, appId)}&refresh=${ordinal}&trigger=${trigger}&prev=${encodeURIComponent(prevAdId)}`);
  slotCache.set(key, pending);
  return pending;
}

// ---------------------------------------------------------------------------
// The slot
// ---------------------------------------------------------------------------

export function AdSlot({
  placement,
  appId,
  minWidth = AD_SLOT_MIN_WIDTH_TITLE_BAR,
  active,
  preview = null,
  className,
  clearance = DEFAULT_CLEARANCE,
  reserveMinWidth = false,
  align = 'center',
  pictureHeight = AD_PICTURE_HEIGHT_TITLE_BAR,
}: AdSlotProps) {
  const t = useT();
  const regionRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const headlineRef = useRef<HTMLSpanElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [width, setWidth] = useState(0);
  const [data, setData] = useState<{ key: string; res: AdSlotResponse | null } | null>(null);
  const [fits, setFits] = useState(true);
  const [open, setOpen] = useState(false);
  const [, setHiddenVersion] = useState(0);
  // The refresh round: the ad fading out under the one fading in, the fade's
  // phase, whether the pointer or focus holds the swap, and how many
  // refreshes this slot asked for since page load.
  const [previous, setPrevious] = useState<{ ad: ServedAd; seq: number } | null>(null);
  const [entered, setEntered] = useState(true);
  const [hover, setHover] = useState(false);
  const [focusWithin, setFocusWithin] = useState(false);
  // The picture: dropped first when the headline would not fit whole beside it, and gone when its bytes never arrived.
  const [pictureDropped, setPictureDropped] = useState(false);
  // The headline gives way whole when only a sliver of it would show (the
  // design pass of 2026-09-30: a long app name left "B…" beside the
  // advertiser on a phone bar).
  const [headlineDropped, setHeadlineDropped] = useState(false);
  const [pictureFailed, setPictureFailed] = useState(false);
  const refreshCount = useRef(0);
  const swapSeq = useRef(0);
  const mounted = useRef(true);

  const key = appId ? `${placement}:${appId}` : null;
  const wide = active && width >= minWidth;
  const wantsFetch = wide && !preview && !!key;

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // Another app in this slot: its refresh count starts over.
  useEffect(() => {
    refreshCount.current = 0;
  }, [key]);

  // Measure the region while active: its LAYOUT width (`slotLayoutWidth`).
  // The border box is what "the free middle offers": the 48 px of clearance
  // each side is inside it. Measured again every time the slot becomes
  // active, which for every canvas placement is the moment Stage or the
  // overview closes (NodeFrame's `adActive` and its siblings include
  // `!canvasCovered`), and on every layout resize the observer reports.
  useEffect(() => {
    const el = regionRef.current;
    if (!active || !el) return;
    const measure = () => setWidth(slotLayoutWidth(el));
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [active]);

  useEffect(() => {
    if (!wantsFetch || !key || !appId) return;
    let cancelled = false;
    void fetchSlot(placement, appId).then((res) => {
      if (!cancelled) setData({ key, res });
    });
    return () => {
      cancelled = true;
    };
  }, [wantsFetch, key, placement, appId]);

  const live = !preview && data && data.key === key ? data.res : null;
  const served: ServedAd | null = preview ?? live?.ad ?? null;
  const token = live?.token ?? null;
  const hidden = served ? hiddenAds.has(served.id) : false;
  const show = wide && !!served && !hidden;
  // The reservation (see `reserveMinWidth`) holds while an answer may still
  // arrive and while an ad is drawn; it lets go the moment the slot resolved
  // to nothing (no app, a 204, an error, or a hidden ad), or the ad's fixed
  // parts did not fit at `minWidth`, so the bar's title takes the width back.
  const resolvedEmpty = !preview && (!key || (!!data && data.key === key && (data.res === null || hidden)));
  const reserve = reserveMinWidth && active && !resolvedEmpty && !(show && !fits);

  const hasPicture = !!served?.image && !pictureFailed;
  const drawPicture = hasPicture && !pictureDropped;

  useEffect(() => {
    setPictureFailed(false);
  }, [served?.image?.url]);

  // WHAT GIVES WAY, IN ORDER (the owner's rule, 2026-09-30): the picture
  // first, when the headline would not fit whole beside it; then the
  // headline truncates, as before; then, if the chip and the advertiser
  // still overflow, the slot stays empty, as before. Measured after layout,
  // again on every width change and every new ad, each time starting over
  // with the picture drawn (`measureKey`), so more room brings it back.
  const measureKey = `${width}|${served?.id ?? ''}|${served?.image?.url ?? ''}`;
  const measuredKey = useRef('');
  useIsomorphicLayoutEffect(() => {
    const el = innerRef.current;
    if (!show || !el) return;
    if (measuredKey.current !== measureKey) {
      measuredKey.current = measureKey;
      if (pictureDropped || headlineDropped) {
        setPictureDropped(false);
        setHeadlineDropped(false);
        return;
      }
    }
    const headline = headlineRef.current;
    if (drawPicture && headline && headline.scrollWidth > headline.clientWidth + 1) {
      setPictureDropped(true);
      return;
    }
    // A truncated headline with room for fewer than about six characters
    // reads as noise ("B…"): the chip and the advertiser stand alone then.
    if (headline && headline.scrollWidth > headline.clientWidth + 1 && headline.clientWidth < MIN_HEADLINE_PX) {
      setHeadlineDropped(true);
      return;
    }
    setFits(rowFits(el));
  }, [show, measureKey, drawPicture, pictureDropped, headlineDropped]);

  // The impression once the ad has rendered, the viewable by the MRC rule on the ad itself (ad-trackers.ts).
  useAdTrackers({ token, elementRef: innerRef, active: show && fits && !preview });

  // THE SWAP (the refresh round): the next answer replaces this ad in place.
  // With motion allowed the old ad fades out under the new one for 200 ms;
  // under reduced motion it is simply replaced. A 204 or an error empties
  // the slot, as a first load would.
  const swapTo = useCallback(
    (res: AdSlotResponse | null, from: ServedAd | null) => {
      if (!mounted.current || !key) return;
      const seq = ++swapSeq.current;
      if (res && from && !prefersReducedMotion()) {
        setPrevious({ ad: from, seq });
        setEntered(false);
        nextFrame(() => {
          if (mounted.current) setEntered(true);
        });
        setTimeout(() => {
          if (mounted.current) setPrevious((p) => (p && p.seq === seq ? null : p));
        }, CROSSFADE_MS);
      } else {
        setPrevious(null);
        setEntered(true);
      }
      setData({ key, res });
    },
    [key],
  );

  const requestNext = useCallback(
    (trigger: AdRefreshTrigger) => {
      if (!key || !appId || !served || preview || refreshCount.current >= MAX_REFRESHES) return;
      const ordinal = ++refreshCount.current;
      const from = served;
      void fetchNextSlot(placement, appId, ordinal, trigger, from.id).then((res) => swapTo(res, from));
    },
    [key, appId, served, preview, placement, swapTo],
  );

  // The refresh clock: viewable time only, deferred while the pointer is
  // over the ad, focus is inside it or the panel is open (ad-refresh.ts).
  useAdRefreshClock({
    elementRef: innerRef,
    eligible: show && fits && !preview && !!token,
    intervalMs: (live?.refresh.intervalSec ?? 0) * 1000,
    idleMs: live?.refresh.idleSec ? live.refresh.idleSec * 1000 : undefined,
    held: hover || focusWithin || open,
    adKey: token,
    onDue: requestNext,
  });

  const close = useCallback(() => {
    setOpen(false);
    triggerRef.current?.focus();
  }, []);

  const hideAd = useCallback(async (): Promise<'hidden' | 'signin' | 'failed'> => {
    if (!served) return 'failed';
    if (!preview) {
      const res = await fetch('/api/ads/hide', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ adId: served.id }),
      }).catch(() => null);
      if (!res) return 'failed';
      if (res.status === 401) return 'signin';
      if (!res.ok) return 'failed';
    }
    hiddenAds.add(served.id);
    if (key) slotCache.set(key, Promise.resolve(null));
    setHiddenVersion((v) => v + 1);
    setOpen(false);
    return 'hidden';
  }, [served, preview, key]);

  const reportAd = useCallback(
    async (reason: AdReportReason): Promise<boolean> => {
      if (preview) return true;
      if (!token) return false;
      const res = await fetch('/api/ads/report', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ token, reason }),
      }).catch(() => null);
      return !!res && res.ok;
    },
    [token, preview],
  );

  const clickUrl = preview ? '#' : live?.clickUrl ?? '#';
  const fade = { transition: `opacity ${CROSSFADE_MS}ms ease-out` } as const;

  /** The picture element, shared by the drawn ad and the fading copy of the last one. */
  const picture = (image: ServedAdImage | undefined, failed: boolean) =>
    image && !failed ? (
      // eslint-disable-next-line @next/next/no-img-element
      <img
        data-ad-picture
        src={image.url}
        alt=""
        aria-hidden
        decoding="async"
        draggable={false}
        referrerPolicy="no-referrer"
        onError={() => setPictureFailed(true)}
        // Not an app icon: a small radius, a hairline border, after the Ad
        // label. The height is the bar's number; the width follows the
        // picture's own aspect ratio up to 1.5 times the height, inside
        // which `object-contain` keeps the ratio rather than cropping.
        className="shrink-0 rounded-[3px] border border-foreground/20 bg-muted/40 object-contain"
        style={{ height: pictureHeight, width: 'auto', maxWidth: Math.round(pictureHeight * AD_PICTURE_MAX_ASPECT) }}
      />
    ) : null;

  return (
    <div
      ref={regionRef}
      data-ad-slot={placement}
      data-ad-state={show ? (fits ? 'shown' : 'no-fit') : 'empty'}
      data-ad-ordinal={show && live ? live.refresh.ordinal : undefined}
      data-ad-picture-state={show && served?.image ? (pictureFailed ? 'failed' : pictureDropped ? 'dropped' : 'shown') : undefined}
      data-ad-headline-state={show ? (headlineDropped ? 'dropped' : 'shown') : undefined}
      // The 48 px of clearance each side (40 here plus the bar's 8 px gap)
      // exists only while an ad is drawn: empty, the region is the same
      // zero-size spacer the title used to stretch into, so a narrow window
      // loses nothing. No fixed height either: the bar's height stays what
      // its title and pills make it.
      className={cn(
        'relative z-[1] min-w-0 flex-1 flex items-center overflow-hidden',
        align === 'start' ? 'justify-start' : 'justify-center',
        show && clearance === DEFAULT_CLEARANCE && 'px-10',
        className,
      )}
      style={{
        // A non-default clearance is inline so the app bar's pair needs no
        // Tailwind class of its own; the default keeps the `px-10` class the
        // title bar shipped with.
        paddingLeft: show && clearance !== DEFAULT_CLEARANCE ? clearance.start : undefined,
        paddingRight: show && clearance !== DEFAULT_CLEARANCE ? clearance.end : undefined,
        minWidth: reserve ? minWidth : undefined,
      }}
    >
      {show && served ? (
        // The drawn ad, and over it (absolute, so it costs no layout) the
        // last ad fading out for the crossfade's 200 ms.
        <div
          className="relative min-w-0 max-w-full"
          // A touch has no hover: a tap's pointerenter would otherwise hold
          // the swap until the next tap elsewhere.
          onPointerEnter={(e) => {
            if (e.pointerType !== 'touch') setHover(true);
          }}
          onPointerLeave={() => setHover(false)}
          onFocusCapture={() => setFocusWithin(true)}
          onBlurCapture={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setFocusWithin(false);
          }}
        >
          <div
            ref={innerRef}
            data-ad-layer="current"
            aria-hidden={!fits}
            // `relative`: the row is its children's offset parent, which is
            // what `rowFits` measures against.
            className={cn('nodrag nopan relative flex min-w-0 max-w-full items-center gap-1.5', !fits && 'invisible')}
            style={{ opacity: entered ? 1 : 0, ...fade }}
          >
            <a
              href={clickUrl}
              target={preview ? undefined : '_blank'}
              rel="sponsored noopener noreferrer"
              title={served.description || undefined}
              tabIndex={fits ? 0 : -1}
              onClick={preview ? (e) => e.preventDefault() : undefined}
              className="relative flex min-w-0 items-center gap-1.5 rounded-sm text-[11px] leading-normal text-foreground/80 after:absolute after:inset-x-0 after:-inset-y-3.5 after:content-[''] hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            >
              <span className="shrink-0 rounded-sm bg-foreground/85 px-1 py-px text-[9px] font-bold uppercase tracking-wide text-background">
                {t('ads.label')}
              </span>
              {drawPicture ? picture(served.image, pictureFailed) : null}
              <span className="shrink-0 whitespace-nowrap font-semibold">{served.advertiser}</span>
              {headlineDropped ? (
                <span className="sr-only">{served.headline}</span>
              ) : (
                <>
                  <span aria-hidden className="shrink-0 text-muted-foreground">
                    ·
                  </span>
                  <span ref={headlineRef} className="min-w-0 truncate whitespace-nowrap">
                    {served.headline}
                  </span>
                </>
              )}
              {!preview && <span className="sr-only">{t('ads.opensInNewTab')}</span>}
            </a>
            <button
              ref={triggerRef}
              type="button"
              aria-label={t('ads.whyThisAd')}
              aria-expanded={open}
              aria-haspopup="dialog"
              tabIndex={fits ? 0 : -1}
              onClick={(e) => {
                e.stopPropagation();
                setOpen((v) => !v);
              }}
              className="relative shrink-0 rounded p-0.5 text-muted-foreground after:absolute after:-inset-y-[13px] after:-left-1.5 after:-right-5 after:content-[''] hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            >
              <Info className="h-3.5 w-3.5" />
            </button>
          </div>
          {previous ? (
            <div
              aria-hidden
              data-ad-layer="previous"
              className="pointer-events-none absolute inset-0 flex items-center gap-1.5 overflow-hidden text-[11px] leading-normal text-foreground/80"
              style={{ opacity: entered ? 0 : 1, ...fade }}
            >
              <span className="shrink-0 rounded-sm bg-foreground/85 px-1 py-px text-[9px] font-bold uppercase tracking-wide text-background">
                {t('ads.label')}
              </span>
              {previous.ad.image && !pictureDropped ? picture(previous.ad.image, false) : null}
              <span className="shrink-0 whitespace-nowrap font-semibold">{previous.ad.advertiser}</span>
              <span className="shrink-0 text-muted-foreground">·</span>
              <span className="min-w-0 truncate whitespace-nowrap">{previous.ad.headline}</span>
            </div>
          ) : null}
        </div>
      ) : null}
      {open && show && served ? (
        <WhyThisAd ad={served} preview={!!preview} anchorRef={triggerRef} onClose={close} onHide={hideAd} onReport={reportAd} />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// "Why this ad"
// ---------------------------------------------------------------------------

const REASON_KEYS: Record<AdReportReason, string> = {
  misleading: 'ads.reasonMisleading',
  offensive: 'ads.reasonOffensive',
  not_relevant: 'ads.reasonNotRelevant',
  other: 'ads.reasonOther',
};

function WhyThisAd({
  ad,
  preview,
  anchorRef,
  onClose,
  onHide,
  onReport,
}: {
  ad: ServedAd;
  preview: boolean;
  anchorRef: React.RefObject<HTMLButtonElement | null>;
  onClose: () => void;
  onHide: () => Promise<'hidden' | 'signin' | 'failed'>;
  onReport: (reason: AdReportReason) => Promise<boolean>;
}) {
  const t = useT();
  const { user } = useAuth();
  const sheet = useCoarsePointer();
  const panelRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const rect = useAnchorRect(!sheet, anchorRef);
  const [hideState, setHideState] = useState<'idle' | 'busy' | 'signin' | 'failed'>('idle');
  const [reason, setReason] = useState<AdReportReason | null>(null);
  const [reportState, setReportState] = useState<'idle' | 'busy' | 'sent' | 'failed'>('idle');

  // Focus in on open; Esc and an outside press close; focus returns to the (i) button (onClose).
  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    const onPress = (e: PointerEvent) => {
      const target = e.target as Node | null;
      if (!target) return;
      if (panelRef.current?.contains(target) || anchorRef.current?.contains(target)) return;
      onClose();
    };
    document.addEventListener('keydown', onKey, true);
    document.addEventListener('pointerdown', onPress, true);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      document.removeEventListener('pointerdown', onPress, true);
    };
  }, [onClose, anchorRef]);

  const canHide = preview || !!user;

  const body = (
    <div ref={panelRef} className="nodrag nopan text-[13px] leading-snug text-foreground" onClick={(e) => e.stopPropagation()}>
      <div className="mb-2 flex items-center justify-between gap-2">
        <h2 className="text-sm font-semibold">{t('ads.whyThisAd')}</h2>
        <button
          ref={closeRef}
          type="button"
          aria-label={t('ads.close')}
          onClick={onClose}
          className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <dl className="space-y-2">
        <div>
          <dd className="font-medium">{t('ads.itIsAnAd')}</dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">{t('ads.advertiser')}</dt>
          <dd>
            {ad.advertiser}
            {ad.displayHost ? <span className="text-muted-foreground"> ({ad.displayHost})</span> : null}
          </dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">{t('ads.paidBy')}</dt>
          <dd>{ad.paidBy}</dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">{t('ads.howChosen')}</dt>
          <dd>
            {ad.signals?.length
              ? ad.category
                ? t('ads.chosenFromAppCategoryCountry', { category: ad.category })
                : t('ads.chosenFromAppCountry')
              : ad.category
                ? t('ads.chosenFromAppCategory', { category: ad.category })
                : t('ads.chosenFromApp')}
          </dd>
          <dd className="text-muted-foreground">{t(ad.signals?.length ? 'ads.notProfiled' : 'ads.notPersonal')}</dd>
        </div>
        {ad.image ? (
          <div>
            <dt className="text-xs text-muted-foreground">{t('ads.pictureLabel')}</dt>
            <dd className="text-muted-foreground">{t('ads.pictureNote')}</dd>
          </div>
        ) : null}
        <div>
          <dt className="text-xs text-muted-foreground">{t('ads.whatYouCanChange')}</dt>
          <dd className="mt-1">
            {canHide ? (
              <button
                type="button"
                disabled={hideState === 'busy'}
                onClick={() => {
                  setHideState('busy');
                  void onHide().then((r) => setHideState(r === 'hidden' ? 'idle' : r));
                }}
                className="inline-flex h-7 items-center rounded-md border border-border px-2.5 text-xs hover:bg-accent disabled:opacity-50"
              >
                {t('ads.hideThisAd')}
              </button>
            ) : (
              <span className="text-muted-foreground">{t('ads.hideSignIn')}</span>
            )}
            {hideState === 'signin' ? <span className="ml-2 text-muted-foreground">{t('ads.hideSignIn')}</span> : null}
            {hideState === 'failed' ? <span className="ml-2 text-destructive">{t('ads.hideFailed')}</span> : null}
          </dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">{t('ads.reportThisAd')}</dt>
          <dd className="mt-1">
            {reportState === 'sent' ? (
              <span>{t('ads.reportThanks')}</span>
            ) : (
              <div className="flex flex-wrap items-center gap-1.5">
                {AD_REPORT_REASONS.map((r) => (
                  <button
                    key={r}
                    type="button"
                    aria-pressed={reason === r}
                    onClick={() => setReason(r)}
                    className={cn(
                      'inline-flex h-7 items-center rounded-full border px-2.5 text-xs',
                      reason === r ? 'border-foreground bg-foreground text-background' : 'border-border hover:bg-accent',
                    )}
                  >
                    {t(REASON_KEYS[r])}
                  </button>
                ))}
                <button
                  type="button"
                  disabled={!reason || reportState === 'busy'}
                  onClick={() => {
                    if (!reason) return;
                    setReportState('busy');
                    void onReport(reason).then((ok) => setReportState(ok ? 'sent' : 'failed'));
                  }}
                  className="inline-flex h-7 items-center rounded-md bg-primary px-2.5 text-xs text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                >
                  {t('ads.sendReport')}
                </button>
                {reportState === 'failed' ? <span className="text-destructive">{t('ads.reportFailed')}</span> : null}
              </div>
            )}
          </dd>
        </div>
      </dl>
    </div>
  );

  if (sheet) {
    return (
      <PopoverPortal role="presentation" className="fixed inset-0 z-[300]">
        <div aria-hidden className="absolute inset-0 bg-black/30" />
        <div className="absolute inset-x-0 bottom-0" role="dialog" aria-modal="true" aria-label={t('ads.whyThisAd')}>
          <BottomSheetShell radiusMode="docked" showHandle safeAreaBottom>
            {body}
          </BottomSheetShell>
        </div>
      </PopoverPortal>
    );
  }

  if (!rect) return null;
  const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : PANEL_WIDTH + 16;
  const left = Math.max(8, Math.min(rect.right - PANEL_WIDTH, viewportWidth - PANEL_WIDTH - 8));
  return (
    <PopoverPortal
      ariaLabel={t('ads.whyThisAd')}
      className="rounded-xl border border-border bg-popover p-3 text-popover-foreground shadow-xl ring-1 ring-black/5 dark:ring-white/10"
      style={{ position: 'fixed', top: rect.bottom + 6, left, width: PANEL_WIDTH, zIndex: 300 }}
    >
      {body}
    </PopoverPortal>
  );
}
